import React, { useState, useEffect } from 'react';
import ApiService from '../../functions/apiService';
import { Paper, Grid, Typography, TextField, Autocomplete, Avatar, CircularProgress, MenuItem, Box } from '@mui/material';
import ButtonTheme from '../../components/ThemeButton/ThemeButton';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Link, useNavigate, useParams } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import EditChecklistHierarchy from './EditChecklistHierarchy';

const ChecklistEditItems = () => {
  let { id } = useParams();
  let navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('checklist-items')
  const [loading, setLoading] = useState(false)
  const [nestedData, setNestedData] = useState([])
	const [targets, setTargets] = useState([])
	const [selectedLocation , setSelectedLocation] = useState('')

	const markSelectedRecursive = (node) => {
		if (node?.in_checklist === 'yes') {
			node.selected = true;
		}

		if (Array.isArray(node.items)) {
			node.items.forEach(item => {
				if (item.in_checklist === 'yes') {
					item.selected = true;
				}
			});
		}

		if (Array.isArray(node.locations)) {
			node.locations.forEach(loc => markSelectedRecursive(loc));
		}

		return node;
	};

  let loadData = () => {
		ApiService.get(`/checklist/edit_items/${id}`)
			.then((data) => {
				const tempObj = data?.data?.data;
				Object.assign(tempObj, { canDisable: false });

				if(tempObj?.items?.length > 0){
					tempObj?.items?.map((item)=>{
						if(item?.in_checklist && item?.in_checklist == 'yes'){
							Object.assign(item, {selected: true})
						}
					})
				}
				
				if (tempObj?.locations?.length > 0) {
					tempObj.locations = tempObj.locations.map(loc => markSelectedRecursive(loc));
				}

				setNestedData(tempObj);
				setSelectedLocation(tempObj?.id);
			})
			.catch((e) => {
				console.error("error");
			});
	};

	useEffect(()=>{  
    loadData();
  }, []);

	const updateChecklist = () => {
		const body = {
			"checklist_id": id,
			"parent_location_id": nestedData?.id,	
			"targets": targets,
		}
		setLoading(true);
		ApiService.post('/checklist/updateItem', body)
			.then((data) => {
				setLoading(false);
				toast.success(data.message)
				navigate("/firehouse/checklist");
			})
			.catch((error) => {
				setLoading(false);
				if (error.status === 422) {
					const errorMessages = Object.keys(error?.data?.errors || {})
						.map((err) => error.data.errors[err][0])
						.filter(Boolean);
			
					if (errorMessages.length) {
						toast.error(
							<div>
								{errorMessages.map((msg, index) => (
									<div key={index}>• {msg}</div>
								))}
							</div>
						);
					}
				} else {
					toast.error(error?.data?.message);
				}
		})
	}

	const buildSubTree = (node) => {
		if (!node.selected) return null;

		const result = {
			description: node.desc || node.desc == '' ? node.desc : (node.checklist_item_description || ''),
			location_id: node.id,
			parent_location_id: node.location_parent_id,
			checklist_item_id: node.checklist_item_id,
			type: node.type || (node.item ? 'item' : 'location'),
		};

		const items = node.items?.filter(i => i.selected).map(item => ({
			description: item.desc || item.desc == '' ? item.desc : (item.checklist_item_description || ''),
			item_location_id: item.id,
			parent_location_id: item.location_id,
			checklist_item_id: item.checklist_item_id,
			type: 'item'
		}));

		const subLocations = node.locations?.map(buildSubTree).filter(Boolean);

		if (items?.length) result["items"] = items;
		if (subLocations?.length) result["sub-location"] = subLocations;

		return result;
	};

	const buildTargets = (rootNode) => {
		const targets = [];

		rootNode.locations?.forEach(child => {
			const target = buildSubTree(child);
			if (target) targets.push(target);
		});

		rootNode.items?.forEach(item => {
			if (item.selected) {
				targets.push({
					description: item.desc || item.desc == '' ? item.desc : (item.checklist_item_description || ''),
					item_location_id: item.id,
					parent_location_id: item.location_id,
					checklist_item_id: item.checklist_item_id,
					type: 'item'
				});
			}
		});

		return targets;
	};

	useEffect(() => {
		const updatedTargets = buildTargets(nestedData);
		setTargets(updatedTargets);
	}, [nestedData]);


  return (
    <>
      {loading ? (
        <Paper className="w-100 h-100 d-flex justify-content-center align-items-center font-fam">
          <CircularProgress />
        </Paper>
      )
        : (
          <Paper className="p-4 font-fam" style={{ marginTop: "1rem" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="h5">
                  <Link to="/firehouse/checklist" style={{ color: "black" }}>
                    <ArrowBackIosIcon />
                  </Link>
                  Edit Checklist Definition</Typography>
              </Grid>

              <Grid item xs={12} md={7} sx={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '195px' }} className={activeTab == 'checklist' ? 'tab-active' : 'tab-white'}
                  onClick={() => {
                    setActiveTab('checklist')
                    navigate(`/firehouse/edit-checklist/${id}`)
                  }}
                >
                  Update Basic Definition
                </div>
                <div style={{ width: '195px' }} className={activeTab == 'checklist-items' ? 'tab-active mx-2' : 'tab-white mx-2'}
                  onClick={() => {
                    setActiveTab('checklist-items')
                    navigate(`/firehouse/edit-checklist-items/${id}`)
                  }}
                >
                  Update Location & Items
                </div>
              </Grid>
            </Grid>

						{nestedData && Object.keys(nestedData).length > 0 &&
							<Box className='mt-5'>
								<EditChecklistHierarchy
									key={nestedData.id}
									propdata={nestedData}
									nestedData = {nestedData}
									loadData={loadData}
									// eslint-disable-next-line no-restricted-globals
									location={location}
									parentSelected={nestedData.selected}
									setNestedData={setNestedData}
									selectedLocation = {selectedLocation}
								/>
							</Box>
						}

						{nestedData && Object.keys(nestedData).length > 0 &&
							<div style={{ marginTop: '40px' , display:'flex' }}>
								<div  
									onClick={() => updateChecklist()}
								>
									<ButtonTheme type='submit' variant="filled" color='primary' style={{ width: "120px", height: "40px" }}>
										Update
									</ButtonTheme>
								</div>

								<div>
									<Link to="/firehouse/checklist">
										<ButtonTheme
											type='submit'
											variant="filled"
											color='#D0D5DD'
											style={{ color: "#FFFFFF", width: "120px", height: "40px", marginLeft:'10px' }}>
											Cancel
										</ButtonTheme>
									</Link>
								</div>
							</div>
						}

						{nestedData && Object.keys(nestedData).length == 0 &&
							<div className='d-flex flex-column align-items-center justify-content-center' style={{ height: 400 }}>
								<img src='/images/blur-logo.png' style={{ height: 200, width: 250 }} />
								<span className='mt-4' style={{ color: '#555555' }}>
									Checklist not found
								</span>
							</div>
						}

          </Paper >
        )
      }
    </>
  )
}
export default ChecklistEditItems;
